import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { usePermissions } from '../context/PermissionsContext';
import Sidebar from '../Sidebar';
import Header from '../Header';

interface Consultant {
  id: string;
  name: string;
  surname: string;
  id_passport_number: string;
  contact_number: string;
  email: string;
  active: boolean;
  verified: boolean;
  start_date: string;
  code: string;
  work_model: string;
  position: string;
  role: string;
}

interface Note {
  id: string;
  note: string;
  created_at: string;
}

interface Document {
  id: string;
  type: string;
  description: string;
  file_path: string;
  created_at: string;
}

interface EditConsultantForm {
  name: string;
  surname: string;
  id_passport_number: string;
  contact_number: string;
}

const API_URL = process.env.REACT_APP_API_URL;

const ConsultantDetails = () => {
  const { can } = usePermissions();
  const { id } = useParams<{ id: string }>();
  const [consultant, setConsultant] = useState<Consultant | null>(null);
  const [notes, setNotes] = useState<Note[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('profile');
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [noteFormData, setNoteFormData] = useState({ note: '' });
  const [noteFormError, setNoteFormError] = useState<string | null>(null);
  const [noteSuccessMessage, setNoteSuccessMessage] = useState<string | null>(null);
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
  const [documentFormData, setDocumentFormData] = useState({ type: '', description: '', file: null as File | null });
  const [documentFormError, setDocumentFormError] = useState<string | null>(null);
  const [documentSuccessMessage, setDocumentSuccessMessage] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState<EditConsultantForm>({
    name: '',
    surname: '',
    id_passport_number: '',
    contact_number: '',
  });
  const [editFormError, setEditFormError] = useState<string | null>(null);
  const [editSuccessMessage, setEditSuccessMessage] = useState<string | null>(null);

  const [deactivateError, setDeactivateError] = useState<string | null>(null);
  const [deactivateSuccessMessage, setDeactivateSuccessMessage] = useState<string | null>(null);

  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [roleFormData, setRoleFormData] = useState({ role: '' });
  const [roleFormError, setRoleFormError] = useState<string | null>(null);
  const [roleSuccessMessage, setRoleSuccessMessage] = useState<string | null>(null);

  const toggleSidebar = () => setIsSidebarVisible(!isSidebarVisible);

  useEffect(() => {
    const fetchConsultant = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('No authentication token found');
        const response = await axios.get(`${API_URL}/api/consultants/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        console.log('✅ Consultant details received:', response.data);
        if (response.status === 200) {
          setConsultant(response.data.data);
          setEditFormData({
            name: response.data.data.name || '',
            surname: response.data.data.surname || '',
            id_passport_number: response.data.data.id_passport_number || '',
            contact_number: response.data.data.contact_number || '',
          });
        } else {
          throw new Error(response.data.message || 'Failed to fetch consultant');
        }
      } catch (error: any) {
        console.error('❌ Error fetching consultant:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
        });
        setError(error.response?.data?.message || 'Failed to load consultant details');
      } finally {
        setIsLoading(false);
      }
    };

    const fetchNotes = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('No authentication token found');
        const response = await axios.get(`${API_URL}/api/consultants/${id}/notes`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        console.log('✅ Notes received:', response.data);
        if (response.status === 200) {
          setNotes(response.data.data || []);
        } else {
          throw new Error(response.data.message || 'Failed to fetch notes');
        }
      } catch (error: any) {
        console.error('❌ Error fetching notes:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
        });
        setError(error.response?.data?.message || 'Failed to load notes');
      }
    };

    const fetchDocuments = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('No authentication token found');
        const response = await axios.get(`${API_URL}/api/consultants/${id}/documents`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        console.log('✅ Documents received:', response.data);
        if (response.status === 200) {
          setDocuments(response.data.data || []);
        } else {
          throw new Error(response.data.message || 'Failed to fetch documents');
        }
      } catch (error: any) {
        console.error('❌ Error fetching documents:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
        });
        setError(error.response?.data?.message || 'Failed to load documents');
      }
    };

    fetchConsultant();
    fetchNotes();
    fetchDocuments();
  }, [id]);

  // Handle edit form input changes
  const handleEditInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
    setEditFormError(null);
  };

  // Handle edit form submission
  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API_URL}/api/consultants/${id}`, editFormData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      console.log('✅ Consultant update response:', response.data);
      if (response.status === 200) {
        setConsultant(prev => prev ? { ...prev, ...editFormData } : null);
        setEditSuccessMessage('Consultant details updated successfully!');
        setIsEditModalOpen(false);
        setTimeout(() => setEditSuccessMessage(null), 3000);
      } else {
        setEditFormError(response.data.message || 'Failed to update consultant');
      }
    } catch (error: any) {
      console.error('❌ Error updating consultant:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      setEditFormError(error.response?.data?.message || 'Failed to update consultant');
    }
  };

    // Handle deactivate consultant
    const handleDeactivateConsultant = async () => {
      if (!window.confirm('Are you sure you want to deactivate this consultant? This will change their status to inactive.')) {
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const response = await axios.put(`${API_URL}/api/consultants/${id}/deactivate`, {}, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        console.log('✅ Consultant deactivation response:', response.data);
        if (response.status === 200) {
          setConsultant(prev => prev ? { ...prev, active: false } : null);
          setDeactivateSuccessMessage('Consultant deactivated successfully!');
          setTimeout(() => setDeactivateSuccessMessage(null), 3000);
        } else {
          setDeactivateError(response.data.message || 'Failed to deactivate consultant');
        }
      } catch (error: any) {
        console.error('❌ Error deactivating consultant:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
        });
        setDeactivateError(error.response?.data?.message || 'Failed to deactivate consultant');
      }
    };

      // Handle role form input changes
      const handleRoleInputChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const { name, value } = e.target;
        setRoleFormData(prev => ({ ...prev, [name]: value }));
        setRoleFormError(null);
      };

  // Handle role form submission
  const handleRoleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!roleFormData.role) {
      setRoleFormError('Please select a role');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API_URL}/api/consultants/${id}`, 
        { role: roleFormData.role },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      console.log('✅ Role update response:', response.data);
      if (response.status === 200) {
        setConsultant(prev => prev ? { ...prev, role: roleFormData.role } : null);
        setRoleSuccessMessage('Role updated successfully!');
        setIsRoleModalOpen(false);
        setTimeout(() => setRoleSuccessMessage(null), 3000);
      } else {
        setRoleFormError(response.data.message || 'Failed to update role');
      }
    } catch (error: any) {
      console.error('❌ Error updating role:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      setRoleFormError(error.response?.data?.message || 'Failed to update role');
    }
  };

      // Open edit modal and initialize form with current data
      const handleOpenEditModal = () => {
        if (consultant) {
          setEditFormData({
            name: consultant.name || '',
            surname: consultant.surname || '',
            id_passport_number: consultant.id_passport_number || '',
            contact_number: consultant.contact_number || '',
          });
        }
        setIsEditModalOpen(true);
        setEditFormError(null);
      };

      // Open role modal and initialize form with current data
      const handleOpenRoleModal = () => {
        if (consultant) {
          setRoleFormData({
            role: consultant.role || '',
          });
        }
        setIsRoleModalOpen(true);
        setRoleFormError(null);
      };

    const handleNoteInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setNoteFormData({ note: e.target.value });
      setNoteFormError(null);
    };

  const handleNoteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!noteFormData.note.trim()) {
      setNoteFormError('Note is required');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/consultants/${id}/notes`, noteFormData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      console.log('✅ Note creation response:', response.data);
      if (response.status === 201) {
        setNotes(prev => [response.data.data, ...prev]);
        setNoteSuccessMessage('Note created successfully!');
        setIsNoteModalOpen(false);
        setNoteFormData({ note: '' });
        setTimeout(() => setNoteSuccessMessage(null), 3000);
      } else {
        setNoteFormError(response.data.message || 'Failed to create note');
      }
    } catch (error: any) {
      console.error('❌ Error creating note:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      setNoteFormError(error.response?.data?.message || 'Failed to create note');
    }
  };

  const handleDocumentInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setDocumentFormData(prev => ({ ...prev, [name]: value }));
    setDocumentFormError(null);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setDocumentFormData(prev => ({ ...prev, file }));
    setDocumentFormError(null);
  };

  const handleDocumentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!documentFormData.type) {
      setDocumentFormError('Document type is required');
      return;
    }
    if (!documentFormData.file) {
      setDocumentFormError('File is required');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('type', documentFormData.type);
      formData.append('description', documentFormData.description);
      formData.append('file', documentFormData.file);
      const response = await axios.post(`${API_URL}/api/consultants/${id}/documents`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      console.log('✅ Document upload response:', response.data);
      if (response.status === 201) {
        setDocuments(prev => [response.data.data, ...prev]);
        setDocumentSuccessMessage('Document uploaded successfully!');
        setIsDocumentModalOpen(false);
        setDocumentFormData({ type: '', description: '', file: null });
        setTimeout(() => setDocumentSuccessMessage(null), 3000);
      } else {
        setDocumentFormError(response.data.message || 'Failed to upload document');
      }
    } catch (error: any) {
      console.error('❌ Error uploading document:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      setDocumentFormError(error.response?.data?.message || 'Failed to upload document');
    }
  };

  const renderTabContent = () => {
    if (isLoading) return <p className="text-gray-700">Loading...</p>;
    if (error) return <p className="text-red-500">{error}</p>;
    if (!consultant) return <p className="text-gray-700">No consultant data available.</p>;

    switch (activeTab) {
      case 'profile':
        return (
          <div>
            {editSuccessMessage && (
              <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4">
                {editSuccessMessage}
              </div>
            )}
            {deactivateSuccessMessage && (
              <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4">
                {deactivateSuccessMessage}
              </div>
            )}
            {deactivateError && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
                {deactivateError}
              </div>
            )}
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Personal Information</h3>
              {can('consultants', 'write') && (
                <div className="flex space-x-2">
                  <button
                    className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                    onClick={handleOpenEditModal}
                  >
                    Update Consultant Details
                  </button>
                  {consultant.active && (
                    <button
                      className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
                      onClick={handleDeactivateConsultant}
                    >
                      Deactivate Consultant
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block font-medium">Name</label>
                <p>{consultant.name || 'N/A'}</p>
              </div>
              <div>
                <label className="block font-medium">Surname</label>
                <p>{consultant.surname || 'N/A'}</p>
              </div>
              <div>
                <label className="block font-medium">Email</label>
                <p>{consultant.email || 'N/A'}</p>
              </div>
              <div>
                <label className="block font-medium">Contact Number</label>
                <p>{consultant.contact_number || 'N/A'}</p>
              </div>
              <div>
                <label className="block font-medium">ID/Passport Number</label>
                <p>{consultant.id_passport_number || 'N/A'}</p>
              </div>
              <div>
                <label className="block font-medium">Active</label>
                <p className={`font-semibold ${consultant.active ? 'text-green-600' : 'text-red-600'}`}>
                  {consultant.active ? 'Yes' : 'No'}
                </p>
              </div>
              <div>
                <label className="block font-medium">Verified</label>
                <p>{consultant.verified ? 'Yes' : 'No'}</p>
              </div>
            </div>
          </div>
        );
      case 'employment':
        return (
          <div>
            {roleSuccessMessage && (
              <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4">
                {roleSuccessMessage}
              </div>
            )}
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Employment Information</h3>
              {can('consultants', 'write') && (
                <button
                  className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                  onClick={handleOpenRoleModal}
                >
                  Update Roles
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block font-medium">Start Date</label>
                <p>{consultant.start_date || 'N/A'}</p>
              </div>
              <div>
                <label className="block font-medium">Code</label>
                <p>{consultant.code || 'N/A'}</p>
              </div>
              <div>
                <label className="block font-medium">Work Model</label>
                <p>{consultant.work_model || 'N/A'}</p>
              </div>
              <div>
                <label className="block font-medium">Position</label>
                <p>{consultant.position || 'N/A'}</p>
              </div>
              <div>
                <label className="block font-medium">Role</label>
                <p className="font-semibold text-blue-600">{consultant.role || 'N/A'}</p>
              </div>
            </div>
          </div>
        );
      case 'notes':
        return (
          <div>
            {noteSuccessMessage && (
              <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4">
                {noteSuccessMessage}
              </div>
            )}
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Notes</h3>
              {can('consultants', 'write') && (
                <button
                  className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                  onClick={() => setIsNoteModalOpen(true)}
                >
                  Create Note
                </button>
              )}
            </div>
            {notes.length === 0 ? (
              <p className="text-gray-500">No notes available.</p>
            ) : (
              <ul className="space-y-4">
                {notes.map(note => (
                  <li key={note.id} className="border p-4 rounded">
                    <p>{note.note}</p>
                    <p className="text-sm text-gray-500 mt-2">
                      Created: {new Date(note.created_at).toLocaleString()}
                    </p>
                  </li>
                ))}
              </ul>
            )}
            {isNoteModalOpen && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                <div className="bg-white p-6 rounded-lg max-w-lg w-full">
                  <h2 className="text-xl font-bold mb-4">Create New Note</h2>
                  <form id="create-note-form" onSubmit={handleNoteSubmit}>
                    <div>
                      <label className="block font-medium mb-1">Note *</label>
                      <textarea
                        name="note"
                        value={noteFormData.note}
                        onChange={handleNoteInputChange}
                        className="w-full border p-2 rounded h-32"
                        placeholder="Enter note"
                      />
                      {noteFormError && <p className="text-red-500 text-sm mt-1">{noteFormError}</p>}
                    </div>
                    <div className="mt-4 flex justify-end space-x-2">
                      <button
                        type="button"
                        className="bg-gray-300 text-black px-4 py-2 rounded hover:bg-gray-400"
                        onClick={() => setIsNoteModalOpen(false)}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                        form="create-note-form"
                      >
                        Submit
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        );

      case 'documents':
        return (
          <div>
            {documentSuccessMessage && (
              <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4">
                {documentSuccessMessage}
              </div>
            )}
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Documents</h3>
              {can('consultants', 'write') && (
                <button
                  className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                  onClick={() => setIsDocumentModalOpen(true)}
                >
                  Upload Document
                </button>
              )}
            </div>
            {documents.length === 0 ? (
              <p className="text-gray-500">No documents available.</p>
            ) : (
              <ul className="space-y-4">
                {documents.map(doc => (
                  <li key={doc.id} className="border p-4 rounded">
                    <p><strong>Type:</strong> {doc.type}</p>
                    <p><strong>Description:</strong> {doc.description || 'N/A'}</p>
                    <p>
                      <a
                        href={`${API_URL}${doc.file_path}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        View Document
                      </a>
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                      Uploaded: {new Date(doc.created_at).toLocaleString()}
                    </p>
                  </li>
                ))}
              </ul>
            )}
            {isDocumentModalOpen && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                <div className="bg-white p-6 rounded-lg max-w-lg w-full">
                  <h2 className="text-xl font-bold mb-4">Upload Document</h2>
                  <form id="upload-document-form" onSubmit={handleDocumentSubmit}>
                    <div className="mb-4">
                      <label className="block font-medium mb-1">Document Type *</label>
                      <select
                        name="type"
                        value={documentFormData.type}
                        onChange={handleDocumentInputChange}
                        className="w-full border p-2 rounded"
                      >
                        <option value="">Select document type</option>
                        <option value="company registration documents">Company Registration Documents</option>
                        <option value="identification document">Identification Document</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div className="mb-4">
                      <label className="block font-medium mb-1">Description</label>
                      <input
                        type="text"
                        name="description"
                        value={documentFormData.description}
                        onChange={handleDocumentInputChange}
                        className="w-full border p-2 rounded"
                        placeholder="Enter description (optional)"
                      />
                    </div>
                    <div className="mb-4">
                      <label className="block font-medium mb-1">File *</label>
                      <input
                        type="file"
                        name="file"
                        onChange={handleFileChange}
                        className="w-full border p-2 rounded"
                        accept=".pdf,.jpg,.jpeg,.png,.gif"
                      />
                      {documentFormError && <p className="text-red-500 text-sm mt-1">{documentFormError}</p>}
                    </div>
                    <div className="mt-4 flex justify-end space-x-2">
                      <button
                        type="button"
                        className="bg-gray-300 text-black px-4 py-2 rounded hover:bg-gray-400"
                        onClick={() => setIsDocumentModalOpen(false)}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                        form="upload-document-form"
                      >
                        Upload
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex">
      <Sidebar isVisible={isSidebarVisible} toggleSidebar={toggleSidebar} />
      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarVisible ? 'ml-64' : 'ml-0'}`}>
        <Header
          title={`Consultant${consultant ? `: ${consultant.name} ${consultant.surname}` : ''}`}
          user={null}
          client={null}
          toggleSidebar={toggleSidebar}
          isSidebarVisible={isSidebarVisible}
          showClientNumber={false}
        />
        <div className="flex-1 p-6 overflow-auto">
          <div className="bg-white rounded-lg shadow-md p-6 w-full max-w-[1600px] mx-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold">Consultant Details</h2>
              <Link to="/consultants" className="text-blue-600 hover:text-blue-800 underline">
                Back to Consultants
              </Link>
            </div>
            <div className="border-b border-gray-200 mb-4">
              <nav className="-mb-px flex space-x-8">
                <button
                  className={`${
                    activeTab === 'profile'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                  onClick={() => setActiveTab('profile')}
                >
                  Profile
                </button>
                <button
                  className={`${
                    activeTab === 'employment'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                  onClick={() => setActiveTab('employment')}
                >
                  Employment Info
                </button>
                <button
                  className={`${
                    activeTab === 'notes'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                  onClick={() => setActiveTab('notes')}
                >
                  Notes
                </button>
                <button
                  className={`${
                    activeTab === 'documents'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                  onClick={() => setActiveTab('documents')}
                >
                  Documents
                </button>
              </nav>
            </div>
            <div>{renderTabContent()}</div>
          </div>
        </div>
      </div>

      {/* Edit Consultant Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">Edit Consultant</h2>
            <form onSubmit={handleEditSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block font-medium mb-1">Name</label>
                  <input
                    type="text"
                    name="name"
                    value={editFormData.name}
                    onChange={handleEditInputChange}
                    className="w-full border p-2 rounded"
                    placeholder="Enter name"
                  />
                </div>
                <div>
                  <label className="block font-medium mb-1">Surname</label>
                  <input
                    type="text"
                    name="surname"
                    value={editFormData.surname}
                    onChange={handleEditInputChange}
                    className="w-full border p-2 rounded"
                    placeholder="Enter surname"
                  />
                </div>
                <div>
                  <label className="block font-medium mb-1">ID/Passport Number</label>
                  <input
                    type="text"
                    name="id_passport_number"
                    value={editFormData.id_passport_number}
                    onChange={handleEditInputChange}
                    className="w-full border p-2 rounded"
                    placeholder="Enter ID or passport number"
                  />
                </div>
                <div>
                  <label className="block font-medium mb-1">Contact Number</label>
                  <input
                    type="text"
                    name="contact_number"
                    value={editFormData.contact_number}
                    onChange={handleEditInputChange}
                    className="w-full border p-2 rounded"
                    placeholder="Enter contact number"
                  />
                </div>
              </div>
              {editFormError && (
                <p className="text-red-500 text-sm mt-4">{editFormError}</p>
              )}
              <div className="mt-6 flex justify-end space-x-2">
                <button
                  type="button"
                  className="bg-gray-300 text-black px-4 py-2 rounded hover:bg-gray-400"
                  onClick={() => setIsEditModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Role Management Modal */}
      {isRoleModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">
              Role Management ({consultant?.name} {consultant?.surname})
            </h2>
            <form onSubmit={handleRoleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block font-medium mb-1">Select Role *</label>
                  <select
                    name="role"
                    value={roleFormData.role}
                    onChange={handleRoleInputChange}
                    className="w-full border p-2 rounded"
                  >
                    <option value="">Select a role</option>
                    <option value="Admin Consultant">Admin Consultant</option>
                    <option value="Assistant">Assistant</option>
                    <option value="Consultant">Consultant</option>
                    <option value="Claims Consultant">Claims Consultant</option>
                    <option value="External Consultant">External Consultant</option>
                  </select>
                  {roleFormError && (
                    <p className="text-red-500 text-sm mt-1">{roleFormError}</p>
                  )}
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-2">
                <button
                  type="button"
                  className="bg-gray-300 text-black px-4 py-2 rounded hover:bg-gray-400"
                  onClick={() => setIsRoleModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                >
                  Save Role
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default ConsultantDetails;